import classNames from 'classnames';
import React, { Children, MutableRefObject, ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { IParentProps } from '../../shared/types/IParentProps';
import { IQATestableProps } from '../../shared/types/IQATestableProps';
import { ISetState } from '../../shared/types/ISetState';
import { IStyleableProps } from '../../shared/types/IStyleableProps';

import { OzTabHeader } from './OzTabHeader';
import { OzTabLayout } from './OzTabLayout';
import { OzTabNavigationLink } from './OzTabNavigationLink';
import { TAB_NAVIGATION_ORIENTATIONS } from './TAB_NAVIGATION_ORIENTATIONS';
import { TAB_NAVIGATION_SIZES } from './TAB_NAVIGATION_SIZES';
import { ITabNavigationContext, TabNavigationContext } from './TabNavigationContext';

export type ITabNavigationProps = IParentProps & IQATestableProps & IStyleableProps & {
    defaultActiveIndex?: number;
    currentActiveIndex?: number;
    onTabChange?: (currentTabIndex: number) => void;
    size?: TAB_NAVIGATION_SIZES;
    orientation?: TAB_NAVIGATION_ORIENTATIONS;
};

/** @deprecated use ITabNavigationProps instead **/
export type ITabProps = ITabNavigationProps;

const FIRST_LINK_INDEX: number = 0;
const INITIAL_ACTIVE_INDEX: number = 0;

function isLinkIndexValid (children: ReactNode, activeIndex: number): boolean {
    return activeIndex <= Children.count(children) - 1;
}

function getActiveIndex (children: ReactNode, defaultActiveKey: number, activeKey: number): number {
    if (!children) {
        return activeKey;
    }

    if (!activeKey && defaultActiveKey !== 0) {
        activeKey = defaultActiveKey;
    }

    if (!isLinkIndexValid(children, activeKey)) {
        return FIRST_LINK_INDEX;
    }

    return activeKey;
}

export function OzTabNavigation (props: ITabNavigationProps): ReactElement | null {
    const {
        children,
        className = '',
        'data-testid': dataTestID = '',
        defaultActiveIndex = 0,
        currentActiveIndex = defaultActiveIndex || 0,
        onTabChange,
        size = TAB_NAVIGATION_SIZES.SMALL,
        orientation = TAB_NAVIGATION_ORIENTATIONS.HORIZONTAL,
    }: ITabNavigationProps = props;

    const initialActiveIndex: number = getActiveIndex(
        children,
        currentActiveIndex,
        INITIAL_ACTIVE_INDEX,
    );

    const [activeIndex, setActiveIndex]: [number, ISetState<number>] = useState<number>(initialActiveIndex);
    const isInitialMount: MutableRefObject<boolean> = useRef(true);

    const context: ITabNavigationContext = useMemo((): ITabNavigationContext => {
        function navigateToTab (currentIndex: number): void {
            setActiveIndex(currentIndex);

            if (onTabChange) {
                onTabChange(currentIndex);
            }
        }

        return {
            activeIndex,
            navigateToTab,
        };
    }, [activeIndex, onTabChange, setActiveIndex]);

    useEffect((): void => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            setActiveIndex(initialActiveIndex);

            if (onTabChange) {
                onTabChange(initialActiveIndex);
            }
        }
    }, [initialActiveIndex, onTabChange]);

    if (!children) {
        return null;
    }

    const layoutClassNames: string = classNames({
        'flex py-3': true,
        'flex-row': orientation === TAB_NAVIGATION_ORIENTATIONS.HORIZONTAL,
        'flex-col': orientation === TAB_NAVIGATION_ORIENTATIONS.VERTICAL,
    });

    return (
        <div className={layoutClassNames} data-testid={dataTestID}>
            <TabNavigationContext.Provider value={context}>
                {Children.map(children, (child: ReactNode, index): ReactElement => {
                    return (
                        <OzTabLayout orientation={orientation} size={size}>
                            <OzTabHeader index={index} size={size} className={className}>{child}</OzTabHeader>
                        </OzTabLayout>
                    );
                })}
            </TabNavigationContext.Provider>
        </div>
    );
}

OzTabNavigation.Link = OzTabNavigationLink;
